/**
 * 拖拽状态机 —— 对应技术设计 §13.1「拖拽器材」。
 *
 * 本文件**不碰 DOM**：输入是世界坐标点，输出是新的 AssemblyGraph。
 * DOM 事件的翻译在 pointer-controller.ts。这样拆是为了让红线 1 / 红线 2 的判定
 * 能在 vitest 里直接跑，不必起浏览器。
 *
 *   pointerdown → begin   选中，提升 zIndex，记录起点
 *   pointermove → move    平移 pose，实时找候选，写 preview 关系
 *   pointerup   → end     有候选就提交；否则落位或弹回
 */

import type { AssemblyGraph } from "../eom/assembly-graph.js";
import { listRelations, updateObject } from "../eom/assembly-graph.js";
import type { Pose } from "../eom/lab-object.js";
import type { Point } from "../eom/port.js";
import type { Relation } from "../eom/relation.js";
import { breakableRelations, solveConstraints } from "./constraint-solver.js";
import {
  clearPreview,
  commit,
  detach,
  setPreview,
  type CommitResult,
} from "./connection-controller.js";
import type { InputProfile } from "./input-profile.js";
import { effectiveSnapRadius } from "./input-profile.js";
import {
  bestSnapCandidate,
  diagnoseSnapFailure,
  type SnapCandidate,
  type SnapContext,
  type SnapFailure,
} from "./snap-controller.js";

/** 拽断阈值：吸附半径的倍数。设成 >1 是为了让「断开」必须是明确意图，不是手抖。 */
const BREAK_RADIUS_FACTOR = 2.2;

export interface DragSession {
  objectId: string;
  /** 按下时的指针世界坐标 */
  pointerStart: Point;
  /** 按下时的器材姿态，弹回与取消要用 */
  poseStart: Pose;
  /** 按下时该器材以 `to` 身份挂着的可断关系 */
  breakable: Relation[];
  /** 是否已越过起手阈值。未越过时当作点击，不算拖拽 */
  moved: boolean;
  /** 已经拽断了（本次拖拽中） */
  detached: boolean;
}

export interface DragMoveResult {
  graph: AssemblyGraph;
  candidate: SnapCandidate | null;
  /** 本次 move 拽断了哪些关系（供上层发事件/提示） */
  broken: Relation[];
}

export interface DragEndResult {
  graph: AssemblyGraph;
  /** 提交成功时的连接结果；没吸上则为 null */
  connected: CommitResult | null;
  /** 未越过起手阈值 = 这是一次点击而不是拖拽 */
  wasClick: boolean;
  /**
   * 没吸上时的**原因**（bug/023 第 2 条）。
   * 只在失败时求值，成功路径不跑诊断。
   */
  failure: SnapFailure | null;
}

const DRAG_Z_INDEX = 999;

export function beginDrag(
  graph: AssemblyGraph,
  objectId: string,
  pointerWorld: Point,
): { graph: AssemblyGraph; session: DragSession } | null {
  const object = graph.nodes[objectId];
  if (!object) return null;
  if (!object.capabilities.includes("movable")) return null;

  const session: DragSession = {
    objectId,
    pointerStart: { ...pointerWorld },
    poseStart: { ...object.pose },
    breakable: breakableRelations(graph, objectId),
    moved: false,
    detached: false,
  };

  const lifted = updateObject(graph, objectId, {
    pose: { ...object.pose, zIndex: DRAG_Z_INDEX },
  });
  return { graph: lifted, session };
}

function offsetOf(session: DragSession, pointerWorld: Point): Point {
  return {
    x: pointerWorld.x - session.pointerStart.x,
    y: pointerWorld.y - session.pointerStart.y,
  };
}

function maxBreakDistance(session: DragSession, graph: AssemblyGraph, profile: InputProfile): number {
  const object = graph.nodes[session.objectId];
  const radii = (object?.ports ?? []).map((port) => port.radius ?? profile.minHitRadius);
  const base = radii.length > 0 ? Math.max(...radii) : profile.minHitRadius;
  return effectiveSnapRadius(base, profile) * BREAK_RADIUS_FACTOR;
}

export function moveDrag(
  graph: AssemblyGraph,
  session: DragSession,
  pointerWorld: Point,
  context: SnapContext,
): DragMoveResult {
  const offset = offsetOf(session, pointerWorld);
  const travelled = Math.hypot(offset.x, offset.y);

  if (!session.moved && travelled < context.profile.dragThreshold) {
    return { graph, candidate: null, broken: [] };
  }
  session.moved = true;

  let next = graph;
  const broken: Relation[] = [];

  // 拽得够远才断开已有连接：手抖不会拆装置，明确拖走才会（§14.1 可恢复但不误触）
  if (!session.detached && session.breakable.length > 0) {
    if (travelled > maxBreakDistance(session, graph, context.profile)) {
      for (const relation of session.breakable) {
        next = detach(next, relation.id);
        broken.push(relation);
      }
      session.detached = true;
    }
  }

  // 还挂着的时候不允许自由拖动：位置由 from 侧的约束决定，只是给一点视觉牵引
  const stillAttached = !session.detached && session.breakable.length > 0;
  const applied: Pose = stillAttached
    ? { ...session.poseStart, zIndex: DRAG_Z_INDEX, x: session.poseStart.x + offset.x * 0.25, y: session.poseStart.y + offset.y * 0.25 }
    : { ...session.poseStart, zIndex: DRAG_Z_INDEX, x: session.poseStart.x + offset.x, y: session.poseStart.y + offset.y };

  next = updateObject(next, session.objectId, { pose: applied });
  next = solveConstraints(next, session.objectId);

  const dragged = next.nodes[session.objectId];
  const candidate = dragged ? bestSnapCandidate(dragged, { ...context, graph: next }) : null;
  next = setPreview(next, candidate, Date.now());

  return { graph: next, candidate, broken };
}

export function endDrag(
  graph: AssemblyGraph,
  session: DragSession,
  context: SnapContext,
  now: number,
): DragEndResult {
  if (!session.moved) {
    return {
      graph: restoreZ(clearPreview(graph), session),
      connected: null,
      wasClick: true,
      failure: null,
    };
  }

  const cleared = clearPreview(graph);
  const dragged = cleared.nodes[session.objectId];
  const candidate = dragged ? bestSnapCandidate(dragged, { ...context, graph: cleared }) : null;

  if (candidate) {
    const result = commit(cleared, candidate, now);

    // 「倒进去 / 放进去」之后，源器材回原位：药品倒进试管，试剂瓶该回托盘，
    // 而不是压在试管身上。容器侧的状态变化由规则负责（hasReagent / hasCotton）。
    const absorbed =
      candidate.bond.constraint === "contain" && candidate.bond.from.objectId === session.objectId;
    const settledGraph = absorbed
      ? updateObject(result.graph, session.objectId, { pose: session.poseStart })
      : result.graph;

    return {
      graph: restoreZ(settledGraph, session),
      connected: result,
      wasClick: false,
      failure: null,
    };
  }

  // 没吸上：拽断过就留在落点，没拽断（说明只是拖了一小段）就弹回起点
  const stillAttached = !session.detached && session.breakable.length > 0;
  const settled = stillAttached
    ? updateObject(cleared, session.objectId, { pose: session.poseStart })
    : cleared;

  const failure = dragged ? diagnoseSnapFailure(dragged, { ...context, graph: cleared }) : null;

  return {
    graph: restoreZ(solveConstraints(settled, session.objectId), session),
    connected: null,
    wasClick: false,
    failure,
  };
}

export function cancelDrag(graph: AssemblyGraph, session: DragSession): AssemblyGraph {
  const restored = updateObject(clearPreview(graph), session.objectId, { pose: session.poseStart });
  return solveConstraints(restored, session.objectId);
}

/** 拖拽结束后把 zIndex 还原成起始值，避免器材永久悬在最上层。 */
function restoreZ(graph: AssemblyGraph, session: DragSession): AssemblyGraph {
  const object = graph.nodes[session.objectId];
  if (!object) return graph;
  return updateObject(graph, session.objectId, {
    pose: { ...object.pose, zIndex: session.poseStart.zIndex ?? 0 },
  });
}

/** 调试/测试用：当前图上有没有残留的 preview 关系。 */
export function hasDanglingPreview(graph: AssemblyGraph): boolean {
  return listRelations(graph).some((relation) => relation.state === "preview");
}
